import type { EditorState, Transaction } from "prosemirror-state";
import { Plugin, PluginKey } from "prosemirror-state";
import { serializeToMarkdown } from "./serializer.ts";

export const submitPluginKey = new PluginKey("submit");

function isInsideCodeBlock(state: EditorState): boolean {
	const { $from } = state.selection;
	for (let d = $from.depth; d > 0; d--) {
		if ($from.node(d).type.name === "code_block") return true;
	}
	return false;
}

export function createSubmitPlugin() {
	return new Plugin({
		key: submitPluginKey,
		state: {
			init() {
				return { onSubmit: null, submitOnEnter: true };
			},
			apply(tr, value) {
				const meta = tr.getMeta(submitPluginKey);
				if (meta) return { ...value, ...meta };
				return value;
			},
		},
		props: {
			handleKeyDown(view, event) {
				const { onSubmit, submitOnEnter } =
					submitPluginKey.getState(view.state) || {};

				const submitCommand = (
					viewState: EditorState,
					dispatch: (tr: Transaction) => void,
				) => {
					const res = onSubmit?.(serializeToMarkdown(viewState.doc).trim());
					if (res instanceof Promise) {
						res.then((shouldClear) => {
							if (shouldClear) {
								view.dispatch(
									view.state.tr.deleteRange(0, view.state.doc.nodeSize - 2),
								);
							}
						});
					} else if (res) {
						dispatch?.(viewState.tr.deleteRange(0, viewState.doc.nodeSize - 2));
					}
					return true;
				};

				// always submit if ctrl is pressed
				if (event.key === "Enter" && event.ctrlKey) {
					return submitCommand(view.state, view.dispatch);
				}

				if (event.key === "Enter" && !event.shiftKey) {
					// Don't auto-submit inside codeblocks
					if (isInsideCodeBlock(view.state)) {
						view.dispatch(view.state.tr.insertText("\n").scrollIntoView());
						return true;
					}

					if (submitOnEnter ?? true) {
						return submitCommand(view.state, view.dispatch);
					} else {
						view.dispatch(view.state.tr.insertText("\n").scrollIntoView());
						return true;
					}
				}

				return false;
			},
		},
	});
}
